import type { SVGProps } from 'react';
import { COST_ICON_PATHS, GLYPH, WORDMARK, type CostIconKey } from '@immoaktas/ui-tokens';

/** Line icons from the design boards (20×20 grid, stroke = currentColor). Decorative unless `title` is given. */
const PATHS = {
  dashboard: 'M3 10 L10 3 L17 10 V17 H3 Z',
  properties: 'M3 3 H9 V9 H3 Z M11 3 H17 V9 H11 Z M3 11 H9 V17 H3 Z M11 11 H17 V17 H11 Z',
  tenants: 'M7 9 a3 3 0 1 0 0.01 0 M2.5 17 a4.5 4 0 0 1 9 0 M14 8 a2.5 2.5 0 1 0 0.01 0 M12.5 13 a4 3.5 0 0 1 5.5 4',
  receipts: 'M5 2.5 H13 L16 5.5 V17.5 H5 Z M8 9 H13 M8 12 H13',
  meters: 'M10 17 a7 7 0 1 1 0.01 0 M10 10 L13 7',
  payments: 'M2.5 5 H17.5 V15 H2.5 Z M2.5 8.5 H17.5 M5.5 12 H8',
  statements: 'M4 2.5 H16 V17.5 H4 Z M7 7 H13 M7 10.5 H13 M7 14 H10',
  history: 'M10 17 a7 7 0 1 1 0.01 0 M10 6 V10 L12.5 12',
  deadlines: 'M3 5 H17 V17 H3 Z M3 8.5 H17 M7 3 V6 M13 3 V6',
  notifications: 'M5 14 V9 a5 5 0 0 1 10 0 V14 L16 15.5 H4 Z M8.5 17.5 H11.5',
  settings: 'M10 13 a3 3 0 1 0 0.01 0 M10 2.5 V5 M10 15 V17.5 M2.5 10 H5 M15 10 H17.5',
  search: 'M9 3 a6 6 0 1 1 0 12 a6 6 0 1 1 0 -12 Z M14 14 L18 18',
  plus: 'M10 4 V16 M4 10 H16',
  arrowRight: 'M4 10 H16 M11 5 L16 10 L11 15',
  check: 'M4 10.5 L8 14.5 L16 6',
  thumbUp: 'M3 9 H6 V17 H3 Z M6 9 L9.5 3 a1.6 1.6 0 0 1 2.8 1.2 L11.5 8 H16 a1.5 1.5 0 0 1 1.5 1.8 L16.3 15.8 a1.5 1.5 0 0 1 -1.5 1.2 H6',
  thumbDown: 'M3 11 H6 V3 H3 Z M6 11 L9.5 17 a1.6 1.6 0 0 0 2.8 -1.2 L11.5 12 H16 a1.5 1.5 0 0 0 1.5 -1.8 L16.3 4.2 a1.5 1.5 0 0 0 -1.5 -1.2 H6',
  alert: 'M10 1.8 a8.2 8.2 0 1 1 0 16.4 a8.2 8.2 0 1 1 0 -16.4 Z M10 6 V11 M10 14.2 V14.3',
  info: 'M10 1.8 a8.2 8.2 0 1 1 0 16.4 a8.2 8.2 0 1 1 0 -16.4 Z M10 9 V14 M10 5.8 V5.9',
  close: 'M5 5 L15 15 M15 5 L5 15',
  chevronDown: 'M5 8 L10 13 L15 8',
  chevronRight: 'M8 5 L13 10 L8 15',
  chevronLeft: 'M12 5 L7 10 L12 15',
  upload: 'M10 13 V3 M6 7 L10 3 L14 7 M3.5 13 V16.5 H16.5 V13',
  download: 'M10 3 V13 M6 9 L10 13 L14 9 M3.5 13 V16.5 H16.5 V13',
  logout: 'M8 3.5 H4 V16.5 H8 M12 6.5 L15.5 10 L12 13.5 M15.5 10 H7.5',
  user: 'M10 3 a3.5 3.5 0 1 1 0 7 a3.5 3.5 0 1 1 0 -7 Z M3.5 17.5 a6.5 5.5 0 0 1 13 0',
  lock: 'M5 9 H15 V17 H5 Z M7 9 V6.5 a3 3 0 0 1 6 0 V9',
  mail: 'M2.5 5 H17.5 V15 H2.5 Z M2.5 5.5 L10 11 L17.5 5.5',
  grid: 'M4 4 H16 V16 H4 Z M4 9 H16 M9 4 V16',
  building: 'M5 17 V6 H15 V17 M7.5 9.5 H9 M11 9.5 H12.5 M7.5 13 H9 M11 13 H12.5',
  home: 'M3 9.5 L10 3.5 L17 9.5 V17 H3 Z',
  edit: 'M4 16 L4.5 12.5 L13 4 L16 7 L7.5 15.5 Z M11.5 5.5 L14.5 8.5',
  trash: 'M4 6 H16 M8 6 V4 H12 V6 M5.5 6 L6.5 17 H13.5 L14.5 6',
  external: 'M11 3.5 H16.5 V9 M16.5 3.5 L9.5 10.5 M14 12 V16.5 H3.5 V6 H8',
  refresh: 'M16 10 a6 6 0 1 1 -1.8 -4.3 M16 3.5 V7 H12.5',
  more: 'M4.5 8.9 a1.1 1.1 0 1 1 0 2.2 a1.1 1.1 0 1 1 0 -2.2 Z M10 8.9 a1.1 1.1 0 1 1 0 2.2 a1.1 1.1 0 1 1 0 -2.2 Z M15.5 8.9 a1.1 1.1 0 1 1 0 2.2 a1.1 1.1 0 1 1 0 -2.2 Z',
  arrowLeft: 'M16 10 H4 M9 5 L4 10 L9 15',
  menu: 'M3 5 H17 M3 10 H17 M3 15 H17',
  allocate: 'M3 6 H17 M3 6 L6 3 M3 6 L6 9 M17 14 H3 M17 14 L14 11 M17 14 L14 17',
  house: 'M2.5 9 L10 3 L17.5 9 V17 H2.5 Z M8 17 V12 H12 V17',
  flats: 'M3 17 V4 H17 V17 Z M6 7 H8 M12 7 H14 M6 10.5 H8 M12 10.5 H14 M8.5 17 V14 H11.5 V17',
  portfolio: 'M2 9 L7 5 L12 9 V17 H2 Z M12 9 L15 6.5 L18 9 V17 H12',
  globe: 'M10 2.5 a7.5 7.5 0 1 1 0 15 a7.5 7.5 0 1 1 0 -15 Z M2.5 10 H17.5 M10 2.5 C7.5 5 7.5 15 10 17.5 C12.5 15 12.5 5 10 2.5',
} as const;
export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 18, strokeWidth = 1.6, title, ...rest }: { name: IconName; size?: number; strokeWidth?: number; title?: string } & Omit<SVGProps<SVGSVGElement>, 'name'>) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} focusable="false" {...rest}>
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  );
}

/** Icon of a cost type (Umlage-Assistent, receipts), same line style; decorative unless `title` is given. */
export function CostIcon({ category, size = 22, strokeWidth = 1.6, title }: { category: string; size?: number; strokeWidth?: number; title?: string }) {
  const d = COST_ICON_PATHS[category as CostIconKey] ?? COST_ICON_PATHS.other_operating;
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden={title ? undefined : true} role={title ? 'img' : undefined} focusable="false">
      {title && <title>{title}</title>}
      <path d={d} />
    </svg>
  );
}

/**
 * Brand mark (design/brand "Haus-Beleg"): rounded tile in brand green with the white house-receipt, two receipt
 * lines and the orange sum line. Fixed brand colours on light and dark backgrounds (brand rule), via CSS classes.
 */
export function Logo({ size = 28, title }: { size?: number; title?: string }) {
  const { line1, line2, sum } = GLYPH;
  return (
    <svg className="brand-logo" width={size} height={size} viewBox={GLYPH.viewBox} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true} focusable="false">
      <rect className="brand-logo-tile" width="100" height="100" rx={GLYPH.tileRadius} />
      <path className="brand-logo-house" d={GLYPH.house} strokeWidth={GLYPH.stroke} strokeLinejoin="round" />
      <rect className="brand-logo-line" {...line1} />
      <rect className="brand-logo-line" {...line2} />
      <rect className="brand-logo-sum" {...sum} />
    </svg>
  );
}

/** Word mark "ImmoAktas" as outlines (Sora 400/700), colour = currentColor: ink on light, white on dark. */
export function Wordmark({ height = 20, label = 'ImmoAktas' }: { height?: number; label?: string }) {
  return (
    <svg className="brand-wordmark" height={height} width={(height * WORDMARK.width) / WORDMARK.height} viewBox={WORDMARK.viewBox} role="img" aria-label={label} focusable="false">
      <path d={WORDMARK.d} fill="currentColor" />
    </svg>
  );
}

/** Ring spinner of the design (track + arc), inherits the text colour. */
export function Spinner({ size = 18, className = '' }: { size?: number; className?: string }) {
  return (
    <svg className={`spinner ${className}`} width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden focusable="false">
      <circle className="track" cx="10" cy="10" r="7.5" strokeWidth="2.5" />
      <path className="arc" d="M10 2.5 a7.5 7.5 0 0 1 7.5 7.5" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
